import React, { FC, ReactNode, useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useOutletContext } from 'react-router-dom';

import { CircleButton, EasyModal, Loading } from '@ui';
import { AnalyticPageOutlet } from '@app/pages/analytics/single-analytic.page';
import connector, { PropsFromRedux } from '@app/utils/store';
import { CompositeMetricData } from '@app/interfaces/metric.type';
import useTranslation from '@app/hooks/use-translation';
import { AllStatusProject } from '@app/interfaces/analytics';
import {
  useCreateProjectMetricGroupMutation,
  useEditProjectMetricGroupMutation,
} from '@app/store/api/metrics.api';
import {
  MetricListArrType,
  MetricObjType,
} from '@app/interfaces/pages-types/anatylics-metric.type';

import {
  useCreateProjectChatMetricGroupMutation,
  useEditProjectChatMetricGroupMutation,
} from '@app/store/api/chat-api/chat-metric.api';

import { useCreateMetricTemplateMutation } from '@app/store/api/metric-template.api';

import { MetricTemplatesGroupType } from '@app/interfaces/slices-types/metric-templates.type';

import { AddToTemplateModal } from '../../analytics-metric-tab/blocks/add-to-template-modal/add-to-template-modal';

import BlocklyBlock from './blockly.block';

const CompositeMetricBlock: FC<
  {
    show: boolean;
    onClose: () => void;
    label: ReactNode;
    gpt_metrics: any[];
    metricSettings: MetricObjType;
    editModal: boolean;
    copyModal: boolean;
    metricList?: MetricListArrType[];
    status?: AllStatusProject | undefined;
    currentTemplate: 'chat' | 'voice';
    page?: string;
    metricTemplates: MetricTemplatesGroupType[] | undefined;
    createFromTemplate?: boolean;
    handleOpenTemplates(): void;
    onCloseCalculated?(): void;
  } & PropsFromRedux
> = (props) => {
  const {
    metricSettings,
    show,
    label,
    onClose,
    handleOpenTemplates,
    editModal,
    currentTemplate,
    gpt_metrics,
    page,
    status,
    onCloseCalculated,
    metricSettingsReset,
    copyModal,
    metricList,
    metricTemplates,
    createFromTemplate,
  } = props;
  const { project } = useOutletContext<AnalyticPageOutlet | undefined>() || {};
  const { t } = useTranslation('pages.metric');
  const { t: tf } = useTranslation('pages.metricTemplates');
  const [createMetricChat] = useCreateProjectChatMetricGroupMutation();
  const [editMetricChat] = useEditProjectChatMetricGroupMutation();
  const [createMetricVoice] = useCreateProjectMetricGroupMutation();
  const [editMetricVoice] = useEditProjectMetricGroupMutation();
  const createMetricMutation = { chat: createMetricChat, voice: createMetricVoice };
  const editMetricMutation = { chat: editMetricChat, voice: editMetricVoice };

  const [calculatedResult, changeCalculatedResult] = useState<CompositeMetricData>({
    formula: { root: undefined },
    metricType: metricSettings?.result_value_type || 'num',
    name: metricSettings?.name || `${project?.name || ''} calculated metric`,
    visualization: metricSettings?.visualization || 'native',
    export: metricSettings?.export,
    dnt_display: metricSettings?.dnt_display,
  });
  const DEFAULT_CALCULATED_RESULT: CompositeMetricData = {
    formula: { root: undefined },
    metricType: 'num',
    name: `${project?.name || ''} calculated metric`,
    visualization: 'native',
  };

  const DEFAULT_CALCULATED_METRIC_DATA = {
    name: metricSettings?.name || `${project?.name || ''} calculated metric`,
    description: metricSettings?.name || `${project?.name || ''} calculated metric`,
    type: 'calculated',
    result_value_type: metricSettings?.result_value_type || 'num',
    settings: {
      formula: {
        root: {
          element: calculatedResult?.formula.root,
        },
      },
    },
  };

  useEffect(() => {
    metricSettings &&
      changeCalculatedResult({
        formula: metricSettings?.settings.formula || undefined,
        metricType: metricSettings?.result_value_type || 'num',
        visualization: metricSettings?.visualization || 'native',
        name: `${metricSettings?.name || project?.name || ''} ${
          copyModal ? tf('copy') : ''
        }`.trim(),
        export: metricSettings?.export,
        dnt_display: metricSettings?.dnt_display,
      });
  }, [copyModal, metricSettings, project?.name, tf]);

  function resetCachedInputFromScreen() {
    document?.querySelector('.blocklyHtmlInput') &&
      document?.querySelector('.blocklyHtmlInput')?.remove();
  }

  async function onCloseHandler() {
    await changeCalculatedResult(DEFAULT_CALCULATED_RESULT);
    await metricSettingsReset();
    resetCachedInputFromScreen();
    onCloseCalculated?.();
    onClose?.();
  }

  const copyToClipboard = useCallback(() => {
    event?.preventDefault();
    const textToCopy = metricSettings?.metric_id;
    if (textToCopy) {
      navigator.clipboard.writeText(textToCopy);
    }
  }, [metricSettings?.metric_id]);

  function metricCalculateDefaultSettings() {
    return {
      metric_settings: {
        ...DEFAULT_CALCULATED_METRIC_DATA,

        export: calculatedResult?.export ||
          metricSettings?.export || {
            export_result: false,
          },

        dnt_display:
          typeof calculatedResult?.dnt_display === 'boolean'
            ? calculatedResult.dnt_display
            : typeof metricSettings?.dnt_display === 'boolean'
            ? metricSettings.dnt_display
            : false,

        name:
          calculatedResult.name ||
          metricSettings?.result_value_type ||
          `${project?.name || ''} calculated metric`,

        order: calculatedResult.visualization === 'native' ? 1000 : 100,

        result_value_type:
          calculatedResult.metricType || metricSettings?.result_value_type || 'num',

        visualization: calculatedResult.visualization || 'native',

        settings: {
          formula: calculatedResult.formula,
        },
      },
    };
  }

  function errorFormulaResponse(calculatedData, status: 'new' | 'edit') {
    changeCalculatedResult((previous) => ({
      ...previous,
      metric_settings: calculatedData.metric_settings,
    }));
    toast.success(
      t(status === 'new' ? 'composite_fail_create_metric' : 'composite_fail_edit_metric'),
    );
  }

  function onSubmitMetricFormula() {
    const calculatedData = metricCalculateDefaultSettings();
    if (project?.project_id) {
      if (editModal && metricSettings?.metric_id) {
        editMetricMutation[currentTemplate || 'voice']({
          params: { metric_id: metricSettings?.metric_id, project_id: project.project_id },
          body: calculatedData.metric_settings as MetricObjType,
        })
          .unwrap()
          .then(() => {
            toast.success(t('composite_success_edit_metric'));
            onClose();
          })
          .catch(() => {
            errorFormulaResponse(calculatedData, 'edit');
          });
      } else {
        createMetricMutation[currentTemplate || 'voice']({
          params: { project_id: project?.project_id },
          body: calculatedData.metric_settings as MetricObjType,
        })
          .unwrap()
          .then(() => {
            toast.success(t('success_create_metric'));
            resetCachedInputFromScreen();
            onClose();
          })
          .catch(() => {
            errorFormulaResponse(calculatedData, 'new');
          });
      }
    }
  }
  const [createMetricTemplate] = useCreateMetricTemplateMutation();
  const [createTemplateModal, setCreateTemplateModal] = useState(false);
  function openAddToTemplateModal() {
    setCreateTemplateModal(true);
  }
  function handleAddToTemplate(templateId: string) {
    const calculatedData = metricCalculateDefaultSettings();
    createMetricTemplate({
      params: { metric_templates_group_id: templateId },
      body: { project_id: project?.project_id, ...calculatedData.metric_settings } as MetricObjType,
    }).then(() => {
      setCreateTemplateModal(false);
    });
  }
  const intermediateStatusesDisable = (status?: AllStatusProject) => {
    if (status === AllStatusProject.FILLING) return true;
    if (status === AllStatusProject.STARTING) return true;
    if (status === AllStatusProject.STOPPING) return true;
    return status === AllStatusProject.ACTIVE;
  };

  if (!metricSettings && editModal && !metricList) return <Loading />;

  return (
    <EasyModal
      show={show}
      onClose={onCloseHandler}
      label={label}
      onAddToTemplate={createFromTemplate || copyModal ? undefined : openAddToTemplateModal}
      onSuccess={onSubmitMetricFormula}
      customLayer={90}
      isAutoClosable={false}
      isNotContentClosable
      onFillFromTemplate={copyModal ? undefined : handleOpenTemplates}
      disabled={page === 'metricTemplate' ? true : intermediateStatusesDisable(status)}
      isFull
    >
      {!show ? (
        <></>
      ) : (
        (metricSettings || !editModal) && (
          <BlocklyBlock
            previousState={calculatedResult}
            gpt_metrics={gpt_metrics}
            onChange={changeCalculatedResult}
            height="100%"
            isEditModal={editModal}
            minWidth="1000"
            createFromTemplate={createFromTemplate}
            metric={metricList || []}
            copyModal={copyModal}
            currentTemplate={currentTemplate}
            metricSettings={
              (editModal || copyModal) && metricSettings
                ? {
                    ...metricSettings,
                    name: `${metricSettings?.name || project?.name || ''} calculated metric ${
                      copyModal ? t('copy') : ''
                    }`.trim(),
                  }
                : createFromTemplate
                ? {
                    ...metricSettings,
                    name: `${metricSettings?.name || project?.name || ''} calculated metric`.trim(),
                  }
                : metricCalculateDefaultSettings().metric_settings
            }
          />
        )
      )}
      <div className="flex items-end justify-end absolute w-[1040px] h-[57px] gap-[40px]">
        <span className="text-0color text-[15px]">{tf('metric_id')}</span>
        <div className="flex gap-1 items-center">
          <p>{metricSettings?.metric_id}</p>
          <CircleButton
            onClick={copyToClipboard}
            icon="CopyIcon"
            className="text-action"
            size={16}
          />
        </div>
      </div>
      <AddToTemplateModal
        metricTemplates={metricTemplates || []}
        show={createTemplateModal}
        name={calculatedResult.name || ''}
        applyBtn={handleAddToTemplate}
        onClose={setCreateTemplateModal}
      />
    </EasyModal>
  );
};

export default connector(CompositeMetricBlock);
